/* eslint-disable no-else-return */
import { computed, useRoute, useRouter } from '@nuxtjs/composition-api';

import {
  goToNextPage as goToNextPageUtil,
  goToPreviousPage,
  isLastPage,
} from '../utils/selfServiceRouting';
import useRegistration from './useRegistration';

import CompanySize from '../components/screens/CompanySize';
import CompanyName from '../components/screens/CompanyName';
import ContactInfo from '../components/screens/ContactInfo';
import Industry from '../components/screens/Industry';
import ZipCode from '../components/screens/ZipCode';
import WhatBroughtYou from '../components/screens/WhatBroughtYou';
import PaymentPage from '../components/screens/PaymentPage';
import HandoffPage from '../components/screens/HandoffPage.vue';
import PlansPage from '../components/screens/PlansPage.vue';
import InstallationPage from '../components/screens/InstallationPage.vue';
import SchedulePage from '../components/screens/SchedulePage.vue';

import SelfServicePages, {
  SelfServicePageSlugs,
  tiersThatGetSentToSelfService,
} from '@/modules/SelfServiceRegistration/constants/SelfServicePages.js';
import usePricePlans from '@/hooks/usePricePlans';

const INDEX_OF_SCHEDULE_CALL_PAGE = 4;

const useSelfService = () => {
  const route = useRoute();
  const router = useRouter();
  const { selectedPlan } = usePricePlans();
  const { state } = useRegistration();
  const isTierZero = computed(() => state.numOfEmployees === '0-0');

  const isSelfService = computed(() => {
    return tiersThatGetSentToSelfService.includes(
      selectedPlan.value?.tierNumber
    );
  });

  const hasValidateToken = computed(() => state.hasValidToken);

  const currentPageIndex = computed(() => {
    return SelfServicePages.findIndex(({ slug }) => {
      return route.value.params.slug === slug;
    });
  });

  const currentPageSlug = computed(() => {
    const page = SelfServicePages.find(
      ({ slug }) => route.value.params.slug === slug
    );

    return page ? page.slug : route.value.params.slug;
  });

  const pageComponent = computed(() => {
    switch (currentPageSlug?.value) {
      case SelfServicePageSlugs.WHAT_BROUGHT_YOU:
        return WhatBroughtYou;
      case SelfServicePageSlugs.COMPANY_SIZE:
        return CompanySize;
      case SelfServicePageSlugs.COMPANY_NAME:
        return CompanyName;
      case SelfServicePageSlugs.ZIP_CODE:
        return ZipCode;
      case SelfServicePageSlugs.INDUSTRY:
        return Industry;
      case SelfServicePageSlugs.CONTACT_INFO:
        return ContactInfo;
      // Recurly payment step (BIO-2148); the BIO-2036 freeze lifts here (BIO-2158).
      case SelfServicePageSlugs.PAYMENT:
        return PaymentPage;
      case SelfServicePageSlugs.HANDOFF:
        return HandoffPage;
      case SelfServicePageSlugs.PLANS:
        return PlansPage;
      case SelfServicePageSlugs.INSTALLATION:
        return InstallationPage;
      case SelfServicePageSlugs.SCHEDULE:
        return SchedulePage;
      default:
        return WhatBroughtYou;
    }
  });

  const showBackButton = computed(() => {
    return (
      currentPageIndex.value > 0 &&
      currentPageIndex.value !== INDEX_OF_SCHEDULE_CALL_PAGE
    );
  });

  const goToNextPage = () => {
    return goToNextPageUtil(currentPageIndex.value, router, null);
  };

  const isCurrentPageLast = computed(() => {
    return isLastPage(currentPageIndex.value);
  });

  return {
    currentPageIndex,
    currentPageSlug,
    goToNextPage,
    goToPreviousPage: () => goToPreviousPage(currentPageIndex.value, router),
    pageComponent,
    showBackButton,
    isSelfService,
    isTierZero,
    hasValidateToken,
    isCurrentPageLast,
  };
};

export default useSelfService;
